// Ein Zettel auf der Tafel — und das Feld, mit dem man einen anheftet.
//
// Farbe sagt die Art, ohne dass man lesen muss:
//   gelb   Aufgabe (zu tun)
//   blau   Termin (zu einer Uhrzeit)
//   rot    Frist (bis zu einem Tag)
// Was der Betrieb selbst stellt, trägt zusätzlich ein kleines „Vom Betrieb".
import { useMemo, useState } from 'react';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import { useAuth } from '../../../gemeinsam/lib/AuthContext';
import Icon, { type IconName } from '../../../gemeinsam/komponenten/Icon';
import { erkennen, type Person } from '../lib/aufgabenEingabe';
import type { Art, Eintrag } from '../lib/tafelDaten';
import { useTafelTexte } from '../lib/useTafel';
import type { Aufgabe } from '../../../gemeinsam/lib/typen';

export const ART_STIL: Record<Art, { zettel: string; punkt: string; text: string; symbol: IconName }> = {
  aufgabe: {
    zettel: 'border-amber-200 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/30',
    punkt: 'bg-amber-400', text: 'text-amber-800 dark:text-amber-300', symbol: 'haken',
  },
  termin: {
    zettel: 'border-sky-200 bg-sky-50 dark:border-sky-900/60 dark:bg-sky-950/30',
    punkt: 'bg-sky-500', text: 'text-sky-800 dark:text-sky-300', symbol: 'uhr',
  },
  frist: {
    zettel: 'border-rose-200 bg-rose-50 dark:border-rose-900/60 dark:bg-rose-950/30',
    punkt: 'bg-rose-500', text: 'text-rose-800 dark:text-rose-300', symbol: 'flagge',
  },
};

export const ARTEN: Art[] = ['aufgabe', 'termin', 'frist'];

/** Runder Haken zum Abhaken — nur bei eigenen Zetteln. */
function Haken({ fertig, beiKlick }: { fertig: boolean; beiKlick: () => void }) {
  const { t } = useSprache();
  return (
    <button type="button" onClick={e => { e.stopPropagation(); beiKlick(); }} aria-pressed={fertig}
            aria-label={fertig ? t('tafel.wiederOffen') : t('tafel.erledigt')}
            className={`mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2 transition ${fertig
              ? 'border-emerald-500 bg-emerald-500 text-white'
              : 'border-slate-300 bg-white/70 text-transparent hover:border-emerald-500 hover:text-emerald-500 dark:border-slate-600 dark:bg-slate-900/50'}`}>
      <Icon name="haken" groesse={11} />
    </button>
  );
}

export function Zettel({ e, beiOeffnen, beiErledigt, ziehbar, mitDatum }: {
  e: Eintrag; beiOeffnen: () => void; beiErledigt?: (fertig: boolean) => void; ziehbar?: boolean; mitDatum?: boolean;
}) {
  const { t } = useSprache();
  const { zettelTexte, tagLang } = useTafelTexte();
  const ich = useAuth().benutzer?.id;
  const { titel, text } = zettelTexte(e);
  const stil = ART_STIL[e.art];
  const a = e.aufgabe;
  const liste = a?.checkliste ?? [];

  return (
    <li draggable={ziehbar}
        onDragStart={ev => { ev.dataTransfer.setData('text/plain', e.schluessel); ev.dataTransfer.effectAllowed = 'move'; }}
        onClick={beiOeffnen}
        onKeyDown={ev => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); beiOeffnen(); } }}
        tabIndex={0} role="button"
        title={ziehbar ? t('tafel.ziehen') : undefined}
        className={`group relative flex cursor-pointer items-start gap-2 rounded-lg border px-2.5 py-2 text-start shadow-sm outline-none transition hover:-translate-y-px hover:shadow focus-visible:ring-2 focus-visible:ring-marke-500 ${stil.zettel} ${e.fertig ? 'opacity-55' : ''} ${e.ueberfaellig ? 'ring-1 ring-red-400 dark:ring-red-700' : ''} ${ziehbar ? 'active:cursor-grabbing' : ''}`}>
      {a && beiErledigt ? <Haken fertig={e.fertig} beiKlick={() => beiErledigt(!e.fertig)} />
        : <span className={`mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center ${stil.text}`}><Icon name={stil.symbol} groesse={14} /></span>}
      <div className="min-w-0 flex-1">
        {(e.uhrzeit || e.ueberfaellig || mitDatum) && (
          <p className={`text-[11px] font-semibold leading-4 ${e.ueberfaellig ? 'text-red-600 dark:text-red-400' : stil.text}`}>
            {mitDatum && e.datum ? `${tagLang(e.datum)}${e.uhrzeit ? ' · ' : ''}` : ''}
            {e.uhrzeit}{a?.bisUhrzeit ? `–${a.bisUhrzeit}` : ''}
            {e.ueberfaellig && `${e.uhrzeit || mitDatum ? ' · ' : ''}${t('aufgaben.ueberfaellig')}`}
          </p>
        )}
        <p className={`hyphens-auto break-words text-[13px] leading-snug ${e.fertig ? 'text-slate-500 line-through' : 'font-medium text-slate-900 dark:text-slate-100'}`}>
          {titel}
        </p>
        {text && <p className="mt-0.5 line-clamp-2 text-[11px] leading-4 text-slate-600 dark:text-slate-400">{text}</p>}
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-slate-500 dark:text-slate-400 empty:hidden">
          {e.quelle !== 'eigen' && <span className="max-w-full truncate rounded bg-white/70 px-1 font-medium dark:bg-slate-900/60">{t('tafel.automatisch')}</span>}
          {a?.priority === 'high' && !e.fertig && <span className="font-semibold text-red-600 dark:text-red-400">! {t('aufgaben.hoch')}</span>}
          {liste.length > 0 && <span>☑ {liste.filter(p => p.erledigt).length}/{liste.length}</span>}
          {a?.wiederholung && <span>↻</span>}
          {a?.assigneeName && a.assigneeId !== ich && <span className="truncate">{a.assigneeName}</span>}
          {a?.timerRunning && <span className="font-semibold text-emerald-600">● {t('aufgaben.laeuft')}</span>}
        </div>
      </div>
    </li>
  );
}

/**
 * Zettel anheften — eine Zeile, wie man es einem Kollegen zuruft:
 * „14:30 Meyer anrufen", „Inventur !hoch @Mia". Eine Uhrzeit macht von selbst
 * einen Termin daraus; die Art lässt sich mit einem Tipp ändern.
 */
export function NeuerZettel({ datum, team, beiAnlegen, beiFertig, autoFokus, gross }: {
  /** Vorgabe für den Tag; die Eingabe („morgen", „15.11.") geht vor. Leer = Ablage. */
  datum: string; team: Person[];
  beiAnlegen: (d: Partial<Aufgabe>) => Promise<Aufgabe | null>;
  beiFertig?: () => void; autoFokus?: boolean; gross?: boolean;
}) {
  const { t, sprache } = useSprache();
  const { tagLang } = useTafelTexte();
  const [text, setText] = useState('');
  const [artGewaehlt, setArtGewaehlt] = useState<Art | null>(null);
  const [sendet, setSendet] = useState(false);
  const erkannt = useMemo(() => erkennen(text, {
    sprache, personen: team,
    woerter: { heute: t('aufgaben.heute'), morgen: t('aufgaben.morgen'), hoch: t('aufgaben.hoch'), niedrig: t('aufgaben.niedrig') },
  }), [text, sprache, team, t]);
  const art: Art = artGewaehlt ?? (erkannt.uhrzeit ? 'termin' : 'aufgabe');
  const tagDavon = erkannt.dueDate ?? datum;

  async function absenden(ev: React.FormEvent) {
    ev.preventDefault();
    if (!erkannt.titel || sendet) return;
    setSendet(true);
    const neu = await beiAnlegen({
      title: erkannt.titel, dueDate: tagDavon, art,
      uhrzeit: erkannt.uhrzeit ?? '', bisUhrzeit: erkannt.bisUhrzeit ?? '',
      priority: erkannt.priority === 'high' ? 'high' : erkannt.priority === 'low' ? 'low' : 'medium',
      ...(erkannt.assigneeId ? { assigneeId: erkannt.assigneeId } : {}),
    });
    setSendet(false);
    if (neu) { setText(''); setArtGewaehlt(null); }
  }

  return (
    <form onSubmit={absenden}
          className={`rounded-lg border border-dashed border-slate-300 bg-white/80 p-1.5 dark:border-slate-700 dark:bg-slate-900/60 ${gross ? 'p-2.5' : ''}`}>
      <div className="flex items-center gap-1.5">
        <input value={text} onChange={e => setText(e.target.value)} autoFocus={autoFokus} maxLength={500}
               onKeyDown={e => { if (e.key === 'Escape') { setText(''); beiFertig?.(); } }}
               onBlur={() => { if (!text.trim()) beiFertig?.(); }}
               placeholder={gross ? t('tafel.schnellGross') : t('tafel.schnell')} aria-label={t('aufgaben.neu')}
               className={`min-w-0 flex-1 bg-transparent px-1 py-1 text-slate-900 outline-none placeholder:text-slate-400 dark:text-slate-100 ${gross ? 'text-sm' : 'text-[13px]'}`} />
        {(gross || text.trim()) && (
          <button type="submit" disabled={!erkannt.titel || sendet} aria-label={t('aktion.hinzufuegen')}
                  className="rounded-md bg-marke-600 p-1 text-white disabled:opacity-40">
            <Icon name="plus" groesse={16} />
          </button>
        )}
      </div>
      {text.trim() && (
        <div className="mt-1 flex flex-wrap items-center gap-1 px-0.5">
          {ARTEN.map(x => (
            <button key={x} type="button" onMouseDown={e => e.preventDefault()} onClick={() => setArtGewaehlt(x)}
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium transition ${art === x
                      ? `${ART_STIL[x].zettel} border ${ART_STIL[x].text}` : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${ART_STIL[x].punkt}`} />{t(`tafel.art.${x}`)}
            </button>
          ))}
          <span className="ms-auto text-[11px] text-slate-500 dark:text-slate-400">
            {tagDavon ? tagLang(tagDavon) : t('tafel.ablage')}
            {erkannt.uhrzeit && ` · ${erkannt.uhrzeit}${erkannt.bisUhrzeit ? `–${erkannt.bisUhrzeit}` : ''}`}
            {erkannt.assigneeName && ` · @${erkannt.assigneeName}`}
            {erkannt.priority === 'high' && ` · !${t('aufgaben.hoch')}`}
          </span>
        </div>
      )}
      {gross && !text.trim() && <p className="mt-1 px-1 text-[11px] text-slate-500 dark:text-slate-400">{t('tafel.schnellHilfe')}</p>}
    </form>
  );
}
